import { formatDate } from '@/utils/date'
import type { LOCALE_CODE } from '~/types/contentful'
import { FALLBACK_LOCALE, PRIMARY_LOCALE, getLocalizedValue } from '@/domains/investor/utils/utils'
import type { IProps, IRawAssetEntry, IRawFileAssetOrArray } from './FileItem.types'

export const useFileItem = (props: IProps) => {
  const { locale } = useI18n()

  const showFormat = computed(() => props.options?.showFormat ?? false)
  const showDate = computed(() => props.options?.showDate ?? false)
  const showSize = computed(() => props.options?.showSize ?? false)

  const contentfulLocale = computed<LOCALE_CODE>(() =>
    locale.value.toLowerCase().startsWith('pl') ? PRIMARY_LOCALE : FALLBACK_LOCALE)

  const normalizeUrl = (value?: string) => {
    if (!value) {
      return ''
    }

    return value.startsWith('//') ? `https:${value}` : value
  }

  const pickFile = (value: IRawFileAssetOrArray) =>
    Array.isArray(value) ? value[0] : value

  const resolveAssetFile = (value: unknown) => {
    const localizedValue = getLocalizedValue(value, contentfulLocale.value) as IRawFileAssetOrArray | IRawAssetEntry | undefined

    if (localizedValue && typeof localizedValue === 'object' && 'fields' in localizedValue) {
      return pickFile(getLocalizedValue(localizedValue.fields?.file, contentfulLocale.value) as IRawFileAssetOrArray)
    }

    return pickFile(localizedValue as IRawFileAssetOrArray)
  }

  const fileAsset = computed(() => {
    const source = props.item.file ?? props.item.fields?.file
    return resolveAssetFile(source)
  })

  const fileUrl = computed(() => normalizeUrl(props.item.url ?? fileAsset.value?.url))

  const title = computed(() =>
    props.item.label
    || props.item.title
    || getLocalizedValue(props.item.fields?.title, contentfulLocale.value) as string | undefined
    || '')

  const date = computed(() => {
    const rawDate = props.item.date ?? getLocalizedValue(props.item.fields?.date, contentfulLocale.value)

    if (typeof rawDate !== 'string' || !rawDate) {
      return ''
    }

    return formatDate(rawDate, 'DD/MM/YYYY')
  })

  const format = computed(() => {
    if (typeof props.item.format === 'string' && props.item.format) {
      return props.item.format.toUpperCase()
    }

    const fileName = fileAsset.value?.fileName ?? fileUrl.value.split(/[?#]/)[0]?.split('/').pop() ?? ''
    const extension = fileName.match(/\.([a-z0-9]{2,5})$/i)?.[1]

    if (extension) {
      return extension.toUpperCase()
    }

    return fileAsset.value?.contentType?.split('/').pop()?.toUpperCase() ?? ''
  })

  const formatBytes = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) {
      return ''
    }

    const units = ['B', 'KB', 'MB', 'GB']
    let normalizedValue = value
    let unitIndex = 0

    while (normalizedValue >= 1024 && unitIndex < units.length - 1) {
      normalizedValue /= 1024
      unitIndex += 1
    }

    const precision = normalizedValue >= 10 || unitIndex === 0 ? 0 : 1
    return `${normalizedValue.toFixed(precision)} ${units[unitIndex]}`
  }

  const size = computed(() => {
    const rawSize = props.item.size ?? props.item.bytes ?? fileAsset.value?.size ?? fileAsset.value?.bytes

    if (typeof rawSize === 'number') {
      return formatBytes(rawSize)
    }

    if (typeof rawSize === 'string') {
      const normalized = rawSize.trim()
      if (!normalized) {
        return ''
      }

      const asNumber = Number(normalized)
      return Number.isFinite(asNumber) ? formatBytes(asNumber) : normalized
    }

    return ''
  })

  return {
    showFormat,
    showDate,
    showSize,
    fileUrl,
    title,
    date,
    format,
    size,
  }
}
